/*  React  */
import { TenantApp } from 'application/Shared/types/tenantApp';
import { useFetchTenantApplicationLink } from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';
import React, { ReactNode, createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';

interface TenantAppContextType {
    loading: boolean;
    refetchTenantApps: () => void;
    tenantApp?: TenantApp;
}

export const TenantAppContext = createContext<TenantAppContextType>({
    loading: false,
    refetchTenantApps: () => {}
});

/**
 * This context is used to provide data for SaaS Ops for a tenant app
 */
export const TenantAppContextProvider = ({
    applicationId,
    params: paramsProp,
    children
}: {
    applicationId: string;
    params?: Record<string, unknown>;
    children: ReactNode;
}) => {
    const { clientId } = useParams();

    const params = useMemo(() => {
        if (paramsProp) {
            return paramsProp;
        }
        if (clientId) {
            return {
                // There are the parameters used in the app details used in the classified table
                // it could be that we don't need all of these, but we don't want mismatched number
                // and it is hard to test which one we don't need given all the places the modal is used.
                applicationId,
                includeActiveUser: true,
                includeAllUncategorized: true,
                includeApplication: true,
                includeApplicationLinkTags: true,
                includeManagementStage: true,
                includePublisher: true,
                includeRemoved: true,
                includeSource: true,
                includeUncataloged: true,
                includeUncategorizedWorkLogins: true,
                includeWorkato: true,
                size: 1
            };
        }
        // For partner level
        return {
            applicationId,
            includeApplication: true,
            includeClients: true,
            includeManagementStage: true,
            size: -1 // To get the data from all the clients
        };
    }, [applicationId, clientId, paramsProp]);

    const {
        loading,
        refetchTenantApps,
        singleTenantApp: tenantApp
    } = useFetchTenantApplicationLink({
        clientId,
        params
    });

    const providerValues = useMemo(
        () => ({
            loading,
            refetchTenantApps,
            tenantApp
        }),
        [loading, refetchTenantApps, tenantApp]
    );

    return (
        <TenantAppContext.Provider value={providerValues}>
            {children}
        </TenantAppContext.Provider>
    );
};

export const useTenantAppContext = () => {
    const context = useContext(TenantAppContext);

    if (context === undefined) {
        throw new Error(
            '`useTenantAppContext` must be used within `TenantAppContextProvider`'
        );
    }

    return context;
};

export const ConditionalTenantAppProvider = ({
    applicationId,
    children,
    params
}: {
    applicationId: string;
    children: React.ReactNode;
    params?: Record<string, unknown>;
}) => {
    const { tenantApp } = useContext(TenantAppContext);

    if (tenantApp) {
        return children;
    }

    return (
        <TenantAppContextProvider applicationId={applicationId} params={params}>
            {children}
        </TenantAppContextProvider>
    );
};
